// src/encounter/services/use-l360-care-model-input-event.ts
// L360: after the encounter saves one of the five care-model INPUT templates (the three plan proposals, the functional
// test clearance, the CPET result review), tell Workflows with ONE event. FIRE-AND-FORGET, like use-l360-lifestyle-intake-event.ts:
// it runs only from the save's onSuccess, returns nothing, and `postL360Event` never throws and shows no error.
//   · only when env.l360EventsEnabled (dev deploy only) and the save returned an id;
//   · only when careModelInputEvent says the template is ready (required fields answered; an illustrative CPET report posts
//     nothing). A draft leaves the last-posted revision as it was;
//   · a plan proposal needs the patient's ONE L360 care plan. It is loaded at post time for save.patientId, and only once the six
//     fields are answered, so autosaves of a half-filled proposal make no request. None, two or more, or a failed load: nothing
//     posts and `posted` stays as it was, so the next save tries again;
//   · the page autosaves every ~1.8 s, so it posts only when the revision (a hash of the answer STATES, not the text)
//     was not posted before for that answer set (else QR id) in this page session. The server is idempotent on the
//     same key too. `posted` is checked again after the plan load, so two overlapping autosaves post once.
import { useCallback, useRef } from 'react';
import { env } from '@/config/env';
import type { FhirQuestionnaire } from '@/types/template-studio-api';
import type { QuestionnaireResponseResource } from '@/types/questionnaire-response-save-api';
import { answerSetRef, careModelInputEvent, isPlanProposalComplete } from '../lib/l360-care-model-inputs';
import { isL360Plan } from '../lib/l360-activation';
import { toCarePlanViews } from '../lib/care-plan-view';
import { fetchCarePlans } from './care-model-api';
import { postL360Event } from './l360-api';

export interface CareModelInputSave {
  questionnaire: FhirQuestionnaire | null | undefined;
  stored: QuestionnaireResponseResource | null | undefined;
  patientId: string | undefined;
  encounterId: string | undefined;
  reviewerId?: string;
}

/** Every revision posted, per answer set id (else stored QR id). Exported so a test can hold its own. */
export type PostedCareModelInputRevisions = Map<string, Set<string>>;

const wasPosted = (posted: PostedCareModelInputRevisions, ref: string, revision: string) => !!posted.get(ref)?.has(revision);
function unpost(posted: PostedCareModelInputRevisions, ref: string, revision: string): void {
  const set = posted.get(ref);
  set?.delete(revision);
  if (set?.size === 0) posted.delete(ref);
}

/** Loads the ids of the patient's ACTIVE L360 care plans. May reject; the sender then posts nothing. */
export type LoadL360PlanIds = (patientId: string) => Promise<string[]>;

export const defaultLoadL360PlanIds: LoadL360PlanIds = async (patientId) =>
  toCarePlanViews(await fetchCarePlans(patientId, undefined))
    // Only an active plan counts: a completed or cancelled L360 plan must not block the proposal of the current one.
    .filter((p) => p.status === 'active' && isL360Plan(p))
    .map((p) => p.carePlanId);

function post(posted: PostedCareModelInputRevisions, save: CareModelInputSave, carePlanId: string | null): void {
  const { questionnaire, stored, patientId } = save;
  if (!stored?.id || !patientId) return;
  const qrId = stored.id;
  // Keyed on the answer set id (else the QR id): a save after a sign is a new row with the same answer set.
  const sourceRef = answerSetRef(stored) ?? qrId;
  const event = careModelInputEvent(questionnaire, stored, { carePlanId, reviewerId: save.reviewerId, qrId });
  if (!event) return;
  const { eventType, payload, revision } = event;
  // Every revision posted is kept, so answers that go A, then B, then back to A do not post A again.
  if (wasPosted(posted, sourceRef, revision)) return;
  // Set BEFORE the request, so a second autosave cannot post the same revision while this one is in flight.
  posted.set(sourceRef, (posted.get(sourceRef) ?? new Set<string>()).add(revision));
  // postL360Event never rejects; the catches are a belt for a future change, so the page can never see an unhandled rejection.
  try {
    void Promise.resolve(postL360Event({ eventType, patientId, sourceRef, revision, payload }))
      .then((result) => {
        // A failed post was not heard, so the next save may send it again. Only if the entry is still this revision.
        if (result?.kind === 'failed') unpost(posted, sourceRef, revision);
      })
      .catch(() => {
        unpost(posted, sourceRef, revision);
      });
  } catch {
    // A synchronous throw is the same: the post was not heard.
    unpost(posted, sourceRef, revision);
  }
}

export function postCareModelInputEvent(
  posted: PostedCareModelInputRevisions,
  save: CareModelInputSave,
  loadPlanIds: LoadL360PlanIds = defaultLoadL360PlanIds,
): void {
  if (!env.l360EventsEnabled) return;
  const { questionnaire, stored, patientId } = save;
  if (!stored?.id || !patientId) return;
  if (!isPlanProposalComplete(questionnaire, stored)) {
    // Clearance and CPET review are synchronous. Any other template, or an open plan field, ends in null inside post().
    post(posted, save, null);
    return;
  }
  // Plan proposal with all six fields answered: load the plans, then post. The load runs for save.patientId, not a page-wide value.
  void Promise.resolve()
    .then(() => loadPlanIds(patientId))
    .then((ids) => {
      if (ids.length === 1) post(posted, save, ids[0]);
    })
    .catch(() => {
      // A failed load posts nothing and leaves `posted` alone; the next save tries again.
    });
}

/**
 * Wraps a plan loader with a per-patient memory: only a load that found exactly one plan is kept, so text edits in a complete
 * proposal do not reload the plans on each autosave. None, several, or a failure loads again on the next save.
 */
export function oneL360PlanMemory(load: LoadL360PlanIds): LoadL360PlanIds {
  const kept = new Map<string, Promise<string[]>>();
  return (patientId) => {
    const hit = kept.get(patientId);
    if (hit) return hit;
    const next = load(patientId);
    kept.set(patientId, next);
    next.then(
      (ids) => ids.length !== 1 && kept.delete(patientId),
      () => kept.delete(patientId),
    );
    return next;
  };
}

/** Returns the one call the page makes from the save's onSuccess. Stable; it holds the posted-revision map and the plan memory. */
export function useL360CareModelInputEvent(): (save: CareModelInputSave) => void {
  const posted = useRef<PostedCareModelInputRevisions>(new Map());
  const load = useRef<LoadL360PlanIds | null>(null);
  return useCallback((save) => {
    load.current ??= oneL360PlanMemory(defaultLoadL360PlanIds);
    postCareModelInputEvent(posted.current, save, load.current);
  }, []);
}
